import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '@l2u/ui/lib/api.ts';
import { effectiveAssignment, scopedOptions } from '../lib/adminAccess.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { canWrite } from '../lib/adminAccess.ts';
import {
  describeSend,
  firstSendableId,
  isManualSendable,
  isQuestionnaireCompliant,
  isResendBlocked,
  optionLabel,
  type SendOption,
} from './sendQuestionnaire.ts';
import { downloadSignedUrl } from '@l2u/ui/lib/download.ts';
import { Avatar } from '@l2u/ui/components/Avatar.tsx';
import { MfaBadge } from '@l2u/ui/components/MfaBadge.tsx';
import { useMfaStatuses } from '../lib/useMfaStatuses.ts';
import { MFA_STATUS_KEY, canResetVendorMfa } from '../lib/mfaStatus.ts';
import { Btn } from '@l2u/ui/components/Btn.tsx';
import { ConfirmModal } from '../components/ConfirmModal.tsx';
import { CenteredSpinner } from '@l2u/ui/components/Spinner.tsx';
import { CompanyStatusBadge, SubmissionStatusBadge } from '@l2u/ui/components/StatusBadge.tsx';
import { ProgressBar } from '@l2u/ui/components/ProgressBar.tsx';
import { ErrorMessage } from '@l2u/ui/components/ErrorMessage.tsx';
import { Modal } from '@l2u/ui/components/Modal.tsx';
import { NavIcon } from '@l2u/ui/components/NavIcon.tsx';
import { Field, Select, TextArea, TextInput } from '@l2u/ui/components/Field.tsx';
import { FileDrop } from '@l2u/ui/components/FileDrop.tsx';
import { DateField } from '@l2u/ui/components/DateField.tsx';
import { FileExpiryControl } from './FileExpiryControl.tsx';
import { DOCUMENT_ACCEPT } from '@l2u/ui/lib/fileTypes.ts';
import { MultiSelect } from '../components/MultiSelect.tsx';
import { StringListEditor } from '../components/StringListEditor.tsx';
import { fromDateInput } from '@l2u/ui/lib/expiry.ts';
import { resendActionForStatus } from '../lib/resendAction.ts';
import { notifyConfirmation, type NotifyChangesResult } from '../lib/notifyConfirmation.ts';
import { effectiveDocStatus } from '@l2u/ui/lib/submissionStatus.ts';
import { hasExpiredLiveFile } from '@l2u/ui/lib/documentVersions.ts';
import { useAbnVerification } from '@l2u/ui/lib/useAbnVerification.ts';
import { useAutoFillField } from '@l2u/ui/lib/autoFillField.ts';
import { AbnStatusIndicator, AbnVerifiedBadge } from '@l2u/ui/components/AbnStatus.tsx';
import { usePortalSettings } from '@l2u/ui/lib/portalSettings.tsx';
import {
  clusterRegionOptions,
  companyInfoLabel,
  contractorTypeOptions,
  stationOptions,
} from '@l2u/ui/lib/companyInfo.ts';
import type {
  AdminQuestionnaire,
  AdminSubmission,
  Company,
  CompanyDocument,
  Question,
  QuestionCategory,
  Submission,
  SubmissionFile,
} from '@l2u/ui/lib/types.ts';
import { computeProgress, type CompletionReason } from '@l2u/ui/lib/visibility.ts';
import { FileAnalysisCard } from './DocumentAnalysis.tsx';
import { orderQuestionsByParent } from '@l2u/ui/lib/questionOrder.ts';
import { outstandingActionCount } from '@l2u/ui/lib/outstandingActions.ts';
import {
  buildCompanyDetailTabs,
  questionsForProgress,
  type CategorySection,
  type ComplianceDocRow,
  type QuestionnaireGroup,
} from '../lib/companyDetailTabs.ts';
import {
  optionalUnanswered,
  questionnaireProgress,
  reviewProgress,
  type QuestionnaireProgress,
  type ReviewProgress,
} from '../lib/companyDetailProgress.ts';
import { instanceOptionLabel } from '@l2u/ui/lib/instanceOption.ts';
import { complianceDocName } from '@l2u/ui/lib/complianceDocName.ts';
import { answerOptionPills, type AnswerOptionPill } from '../lib/answerOptions.ts';
import { useReviewMutation } from './useReviewMutation.ts';
import { Tabs, type TabItem } from '@l2u/ui/components/Tabs.tsx';
import { CommentModal } from './CommentModal.tsx';
import { adminDocumentsTabApprovals } from './documentApprovals.ts';
import { awaitingAdminReply, awaitingReplyCount } from './awaitingReply.ts';
import { NotCompliantModal } from './NotCompliantModal.tsx';
import { DocumentReviewTable } from './DocumentReviewTable.tsx';
import { ComplianceDocStats } from './ComplianceDocStats.tsx';
import { complianceDocStats } from './complianceDocStats.ts';
import { CompanyActivityTab } from './CompanyActivityTab.tsx';
import {
  isAwaitingAdmin,
  reviewableSubmissions,
  reviewTurn,
  ADMIN_REVIEW_LABEL,
} from '@l2u/ui/lib/reviewState.ts';
import { frozenApprovedDoc } from '@l2u/ui/lib/frozenApprovedDoc.ts';
import { abnNeedsAction } from '../lib/abnAction.ts';
import { gstBlocksCompliance } from '../lib/gstBlocksCompliance.ts';
import { legalNameDisplay } from '../lib/legalNameDisplay.ts';
import { companyHeading } from '../lib/companyHeading.ts';
import { invalidateCompany } from '../lib/invalidateCompany.ts';
import { selectEffectiveQuestions, type SnapshotRow } from '@l2u/shared/effectiveQuestions';
import { ExportQuestionnaireButton } from './ExportQuestionnaireButton.tsx';
import { FileHistoryModal } from './FileHistoryModal.tsx';

// Synthetic keys for the fixed tabs. Namespaced with underscores so they can
// never collide with a real questionnaire UUID.
const COMPANY_INFO_TAB = '__company_info__';
const COMPLIANCE_DOCS_TAB = '__compliance_docs__';
const ACTIVITY_TAB = '__activity_log__';
const QUESTIONNAIRES_TAB = '__questionnaires__';
const ADDITIONAL_TAB = '__additional_questions__';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function AdminCompanyDetail() {
  const { id = '' } = useParams();
  const location = useLocation();
  // "← Vendors" always returns to the vendors list (matching its label) and
  // carries back the page + filters the admin left off on — the list's row link
  // stashes them in history state (they also live in the list URL). Reached from
  // elsewhere (Dashboard, Expiring Documents), it lands on the unfiltered list.
  const backToVendors = `/vendors${
    (location.state as { vendorsListSearch?: string } | null)?.vendorsListSearch ?? ''
  }`;
  const qc = useQueryClient();
  const [notifyResult, setNotifyResult] = useState<NotifyChangesResult | null>(null);

  const companyQuery = useQuery({
    queryKey: ['company', id],
    queryFn: () => adminApi.get<{ company: Company }>(`/api/companies/${id}`),
    enabled: !!id,
  });
  const submissionsQuery = useQuery({
    queryKey: ['company', id, 'submissions'],
    queryFn: () =>
      adminApi.get<{
        submissions: AdminSubmission[];
        question_snapshot: SnapshotRow[];
        frozen_questionnaire_ids: string[];
      }>(`/api/companies/${id}/submissions`),
    enabled: !!id,
  });
  const questionsQuery = useQuery({
    queryKey: ['questions'],
    queryFn: () => adminApi.get<{ questions: Question[] }>('/api/questions'),
  });
  const documentsQuery = useQuery({
    queryKey: ['documents'],
    queryFn: () => adminApi.get<{ documents: CompanyDocument[] }>('/api/documents'),
  });
  const categoriesQuery = useQuery({
    queryKey: ['question-categories', 'all'],
    queryFn: () =>
      adminApi.get<{ categories: QuestionCategory[] }>(
        '/api/question-categories?include_archived=1',
      ),
  });
  const questionnairesQuery = useQuery({
    queryKey: ['company', id, 'questionnaires'],
    queryFn: () =>
      adminApi.get<{ questionnaires: AdminQuestionnaire[] }>(`/api/companies/${id}/questionnaires`),
    enabled: !!id,
  });

  // Which "email sent" confirmation is open: the /invite flow or the plain
  // login-link flow (null = closed).
  const [sentModal, setSentModal] = useState<'invite' | 'login_link' | null>(null);
  const inviteMutation = useMutation({
    mutationFn: () => adminApi.post(`/api/companies/${id}/invite`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company', id] });
      setSentModal('invite');
    },
  });
  // Vendors past 'invited' get a short-lived magic login link instead — unlike
  // /invite this never resets status or re-snapshots questions.
  const resendLoginMutation = useMutation({
    mutationFn: () => adminApi.post<{ sent: boolean }>(`/api/companies/${id}/resend-login-link`),
    onSuccess: () => setSentModal('login_link'),
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Task 11: confirm before sending invite / login-link email.
  const [resendConfirm, setResendConfirm] = useState<'invite' | 'login' | null>(null);

  // Vendor 2FA. The same batched endpoint the list uses, asked for one id — so
  // there is one code path to keep correct, not two.
  const { user } = useAdminAuth();
  // Presentation only -- requireWriteRole on the API is the enforcement.
  const mayWrite = canWrite(user?.role);
  const mfaStatuses = useMfaStatuses(id ? [id] : []);
  const mfaEnrolled = mfaStatuses.get(id);
  const [mfaResetConfirm, setMfaResetConfirm] = useState(false);
  const [mfaResetResult, setMfaResetResult] = useState<{ removed: number; total: number } | null>(
    null,
  );
  const mfaResetMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ ok: true; removed: number; total: number }>(`/api/companies/${id}/mfa-reset`),
    onSuccess: (result) => {
      // Report what the API actually did. A partial reset still leaves the
      // vendor locked out, so "done" would be a lie the admin acts on.
      setMfaResetResult({ removed: result.removed, total: result.total });
      // Prefix invalidate: ['company', id] also covers the activity tab's
      // ['company', id, 'activity', …], so the audit row appears immediately.
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: [MFA_STATUS_KEY] });
    },
  });
  const deactivateMutation = useMutation({
    mutationFn: () => adminApi.post<{ company: Company }>(`/api/companies/${id}/deactivate`),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });
  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.post<{ company: Company }>(`/api/companies/${id}/reactivate`),
    onSuccess: () => {
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ['company', id] });
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const pendingChangesQuery = useQuery({
    queryKey: ['company', id, 'pending-changes'],
    queryFn: () => adminApi.get<{ count: number }>(`/api/companies/${id}/notify-changes`),
    enabled: !!id,
  });

  const notifyChangesMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ sent: boolean; count: number; to?: string }>(
        `/api/companies/${id}/notify-changes`,
      ),
    onSuccess: (result) => {
      // Record what the API actually did. It answers sent:false when it declined
      // to email anyone, and reporting that as a send is what made a partial
      // review look like a mail-delivery failure.
      setNotifyResult({ sent: result.sent, count: result.count });
      // Publishing the review recomputes the vendor's overall status and flips
      // draft submission verdicts to live server-side, so refetch the company,
      // its submissions, and the vendors list to reflect the new status without
      // a manual page refresh. Mirrors useReviewMutation / deactivate.
      invalidateCompany(qc, id);
      void qc.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  const pendingChangesCount = pendingChangesQuery.data?.count ?? 0;
  // What to show beside the Notify button, derived from the API's own
  // {sent} answer rather than from the fact that we asked.
  const notifyMessage = notifyConfirmation(notifyResult, pendingChangesCount);

  const documents = documentsQuery.data?.documents ?? [];
  const docMap = useMemo(() => {
    const m = new Map<string, CompanyDocument>();
    for (const d of documents) m.set(d.id, d);
    return m;
  }, [documents]);
  const documentName = (docId: string) => docMap.get(docId)?.name ?? docId.slice(0, 8);

  const submissions = submissionsQuery.data?.submissions ?? [];
  const awaitingReplyTotal = awaitingReplyCount(submissions);
  const questions = questionsQuery.data?.questions ?? [];
  const questionSnapshot = submissionsQuery.data?.question_snapshot ?? [];
  const frozenQuestionnaireIds = useMemo(
    () => new Set(submissionsQuery.data?.frozen_questionnaire_ids ?? []),
    [submissionsQuery.data?.frozen_questionnaire_ids],
  );
  // Freeze: for a submitted questionnaire show only its snapshotted questions
  // (incl. retired ones, for fading); unsubmitted questionnaires show the live
  // active set. Same rule the vendor API applies server-side.
  // Ordered so each conditional question sits directly beneath the question it
  // depends on (task 5), matching the vendor's wizard. buildCompanyDetailTabs
  // preserves input order when bucketing, so this orders every review row.
  const effectiveQuestions = useMemo(
    () =>
      orderQuestionsByParent(
        selectEffectiveQuestions(questions, questionSnapshot, frozenQuestionnaireIds),
      ),
    [questions, questionSnapshot, frozenQuestionnaireIds],
  );
  const subByQ = useMemo(() => {
    const m = new Map<string, AdminSubmission>();
    for (const s of submissions) m.set(s.question_id, s);
    return m;
  }, [submissions]);

  // Question ids whose submission is approved (marked compliant) — drives which
  // compliance docs are mirrored into the Compliance Documents tab. They also
  // stay inline in their questionnaire (mirrors isApprovedComplianceDoc on the
  // vendor side).
  const approvedIds = useMemo(
    () => new Set(submissions.filter((s) => s.status === 'approved').map((s) => s.question_id)),
    [submissions],
  );

  // Question ids in a renewal cycle (vendor replaced an expired approved doc;
  // admin owes a re-verification) — pinned in the Compliance Documents tab
  // alongside the approved ones, where the re-review buttons render.
  const reverifyIds = useMemo(
    () =>
      new Set(submissions.filter((s) => s.reverify_requested_at != null).map((s) => s.question_id)),
    [submissions],
  );

  const questionById = useMemo(() => new Map(questions.map((x) => [x.id, x])), [questions]);

  // Headings are the live category names: since 2026-11-02 every vendor sees
  // the live name too (frozen or not), so this matches what the vendor sees.
  const categories = useMemo(
    () => categoriesQuery.data?.categories ?? [],
    [categoriesQuery.data?.categories],
  );
  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];

  // Progress counts only questions the vendor can actually be asked —
  // applicable questionnaires plus orphans — matching the vendor portal's
  // server-side computation (see questionsForProgress).
  const progressQuestions = useMemo(
    () => questionsForProgress(questionnaires, categories, effectiveQuestions),
    [questionnaires, categories, effectiveQuestions],
  );
  const progress = useMemo(
    () => computeProgress(progressQuestions, submissions as unknown as Submission[]),
    [progressQuestions, submissions],
  );

  const answeredIds = useMemo(() => new Set(submissions.map((s) => s.question_id)), [submissions]);

  // Tab model: applicable questionnaires become tabs (category sections,
  // including compliance docs shown inline), retired-but-answered
  // questionnaires collapse into one Questionnaires tab bucket, and approved
  // compliance docs are additionally mirrored into the Compliance Documents rows.
  const tabModel = useMemo(
    () =>
      buildCompanyDetailTabs(
        questionnaires,
        categories,
        effectiveQuestions,
        progress.visibleIds,
        answeredIds,
        approvedIds,
        reverifyIds,
      ),
    [
      questionnaires,
      categories,
      effectiveQuestions,
      progress.visibleIds,
      answeredIds,
      approvedIds,
      reverifyIds,
    ],
  );
  const [activeTab, setActiveTab] = useState<string>(COMPANY_INFO_TAB);
  // Which grouped questionnaire is shown in the consolidated "Questionnaires"
  // tab. '' = none selected → no questions shown until the admin picks one.
  const [selectedGroupedId, setSelectedGroupedId] = useState<string>('');
  const [uploadDocOpen, setUploadDocOpen] = useState(false);
  const [sendQOpen, setSendQOpen] = useState(false);

  if (
    companyQuery.isLoading ||
    submissionsQuery.isLoading ||
    questionsQuery.isLoading ||
    categoriesQuery.isLoading ||
    documentsQuery.isLoading ||
    questionnairesQuery.isLoading
  ) {
    return <CenteredSpinner />;
  }
  const company = companyQuery.data?.company;
  if (!company) {
    return <ErrorMessage error={new Error('Vendor not found')} />;
  }

  const isDeactivated = company.status === 'deactivated';

  // Manual+active questionnaires offered in the Send dialog, each flagged
  // compliant when the vendor's submissions for it are all approved — the same
  // signal as the per-questionnaire "Compliant" pill (reviewTurn). Compliant
  // ones are shown but disabled, so they cannot be resent.
  const sendOptions: SendOption[] = questionnaires.filter(isManualSendable).map((q) => {
    // reviewableSubmissions drops compliance-exempt and hidden questions, so
    // this compliance signal matches the per-questionnaire "Compliant" pill
    // exactly (one source of truth).
    const groupQuestionsForSend = effectiveQuestions.filter((eq) => eq.questionnaire_id === q.id);
    return {
      id: q.id,
      name: q.name,
      assigned: q.assigned,
      sent_at: q.sent_at,
      compliant: isQuestionnaireCompliant(groupQuestionsForSend, subByQ, progress.visibleIds),
    };
  });

  // "Vendor information" leads the strip, then one tab per applicable
  // questionnaire (own-tab), then Questionnaires (consolidated grouped +
  // orphans), then Compliance Documents, then Activity log.
  const hasGrouped = tabModel.groupedTab.length > 0;
  const hasOrphans = tabModel.orphanQuestions.length > 0;

  // Per-questionnaire dropdown badges use the same outstanding-actions metric
  // as every other tab badge (pending/rejected submissions + required, visible,
  // unanswered questions), so the dropdown items, their tab total, and the
  // sibling own-tab/Compliance badges all count the same thing.
  const outstandingCount = (qs: Question[]) =>
    outstandingActionCount(qs, progress.completionByQuestion, subByQ, { skipFrozen: true });

  const questionnaireMenuItems = [
    ...tabModel.groupedTab.map((g) => ({
      key: g.questionnaire.id,
      label: g.questionnaire.name,
      badge: outstandingCount(groupQuestions(g)),
    })),
    ...(hasOrphans
      ? [
          {
            key: ADDITIONAL_TAB,
            label: 'Additional questions',
            badge: outstandingCount(tabModel.orphanQuestions),
          },
        ]
      : []),
  ];

  // Red needs-action pill on the Compliance Documents tab: number of EXPIRED
  // document rows. Same complianceDocStats call and same input as the
  // <ComplianceDocStats> cards inside the tab panel, so the pill always equals
  // the "Expired" card.
  const complianceExpiredCount = complianceDocStats(
    tabModel.complianceDocs.map((r) => subByQ.get(r.question.id)),
  ).expired;

  const tabItems: TabItem[] = [
    {
      key: COMPANY_INFO_TAB,
      label: 'Vendor information',
      // A red "1" flags an ABN needing action (verdict = invalid), mirroring the
      // badge inside the tab; it clears once the ABN is re-verified.
      badge: abnNeedsAction(company.abn_verification_status) ? 1 : 0,
      badgeClassName: 'bg-red-500 text-white',
    },
    ...tabModel.questionnaireTabs.map((g) => ({
      key: g.questionnaire.id,
      label: g.questionnaire.name,
      badge: outstandingActionCount(groupQuestions(g), progress.completionByQuestion, subByQ, {
        skipFrozen: true,
      }),
    })),
    ...(hasGrouped || hasOrphans
      ? [
          {
            key: QUESTIONNAIRES_TAB,
            label: 'Questionnaires',
            // The tab total is the sum of the per-questionnaire item badges
            // above, so the closed-tab count always matches the breakdown the
            // dropdown reveals. (`?? 0` guards a future badge-less item from
            // poisoning the sum with NaN.)
            badge: questionnaireMenuItems.reduce((sum, item) => sum + (item.badge ?? 0), 0),
            menu: {
              items: questionnaireMenuItems,
              selectedKey: selectedGroupedId,
              // Selecting a questionnaire is what navigates into the
              // Questionnaires panel — clicking the tab alone only opens this
              // dropdown (see MenuTab.handleClick).
              onSelectItem: (key: string) => {
                setSelectedGroupedId(key);
                setActiveTab(QUESTIONNAIRES_TAB);
              },
            },
          },
        ]
      : []),
    {
      key: COMPLIANCE_DOCS_TAB,
      label: 'Compliance Documents',
      badge: outstandingActionCount(
        tabModel.complianceDocs.map((r) => r.question),
        progress.completionByQuestion,
        subByQ,
      ),
      alertBadge: complianceExpiredCount,
    },
    { key: ACTIVITY_TAB, label: 'Activity log' },
  ];
  // Fall back to Vendor information when the stored key no longer matches a
  // tab (e.g. a questionnaire was deactivated between refetches).
  const activeKey = tabItems.some((t) => t.key === activeTab) ? activeTab : COMPANY_INFO_TAB;
  const activeGroup = tabModel.questionnaireTabs.find((g) => g.questionnaire.id === activeKey);
  // Resolve the dropdown selection for the consolidated "Questionnaires" tab.
  // Returns undefined when nothing is selected or the id went stale between
  // refetches (e.g. the questionnaire was deactivated) → empty state.
  const selectedGroupedGroup = tabModel.groupedTab.find(
    (g) => g.questionnaire.id === selectedGroupedId,
  );

  const heading = companyHeading(company.company_name, company.abn_entity_name);

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Link to={backToVendors} className="text-sm text-slate-500 hover:text-slate-700">
          ← Vendors
        </Link>

        {/* Header row: identity on the left, primary actions aligned on the
            right of the same row. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Identity: avatar, then the company name with its status beside it
              and the primary contact + email on the line underneath. */}
          <div className="flex items-start gap-4">
            <span className="relative inline-flex shrink-0">
              <Avatar name={company.company_name} size={56} />
              <MfaBadge enabled={mfaEnrolled} />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-3">
                {/* The vendor legal name the admin entered, with the ABR
                    registry's entity name beside it when the two disagree — a
                    discrepancy the admin would otherwise only find by scrolling
                    to the Vendor information tab's Legal entity name row. */}
                <h1 className="text-2xl font-semibold text-slate-900">
                  {heading.name}
                  {heading.registryName && (
                    <span className="ml-2 text-base font-normal text-slate-500">
                      ({heading.registryName})
                    </span>
                  )}
                </h1>
                <CompanyStatusBadge status={company.status} />
                {abnNeedsAction(company.abn_verification_status) && (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    ABN needs action
                  </span>
                )}
                {/* A confirmed "not registered" is a hard compliance blocker on
                    the server, so surface it beside the status badge. Not gated
                    on company.status: it is the mid-flow 'review' vendors whose
                    reviewers cannot otherwise tell why approving every question
                    never turns the vendor compliant. */}
                {gstBlocksCompliance(company.gst_registered) && (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    GST not registered
                  </span>
                )}
                {awaitingReplyTotal > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                    <NavIcon name="message-square" size={12} />
                    {awaitingReplyTotal} awaiting your reply
                  </span>
                )}
              </div>
              <div className="mt-1 text-sm text-slate-600">
                <span className="font-medium text-slate-700">{company.contact_name}</span>
                <span className="mx-2 text-slate-300">·</span>
                <a
                  href={`mailto:${company.email}`}
                  className="text-slate-500 hover:text-slate-700 hover:underline"
                >
                  {company.email}
                </a>
              </div>
            </div>
          </div>

          {/* Actions, aligned with the company name and avatar. */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {notifyMessage !== null && (
              <span
                className={`text-xs ${
                  notifyMessage.tone === 'success' ? 'text-emerald-600' : 'text-slate-500'
                }`}
              >
                {notifyMessage.text}
              </span>
            )}
            {mfaResetResult !== null && (
              <span className="text-xs text-slate-500">
                {mfaResetResult.removed === mfaResetResult.total
                  ? '2FA reset — the vendor sets it up again at next sign-in'
                  : `Only ${mfaResetResult.removed} of ${mfaResetResult.total} factors removed — try again`}
              </span>
            )}
            {/* Offered only for a vendor who actually holds a factor, and never
                below a super admin — mirrors requireSuperAdminRole on the route,
                so the button is never shown to someone the API would refuse.
                Not gated on isDeactivated: clearing a factor is part of
                offboarding too, and the route does not 409 it. */}
            {canResetVendorMfa(user?.role, mfaEnrolled) && (
              <Btn size="sm" variant="info" onClick={() => setMfaResetConfirm(true)}>
                Reset 2FA
              </Btn>
            )}
            {mayWrite && !isDeactivated && (
              <>
                {/* All lifecycle actions pause while deactivated (the API 409s them
                    anyway — this keeps the UI honest). */}
                {resendActionForStatus(company.status, company.invited_at ?? null) === 'invite' ? (
                  <Btn
                    size="sm"
                    variant="info"
                    onClick={() => setResendConfirm('invite')}
                    loading={inviteMutation.isPending}
                  >
                    {company.invited_at ? 'Resend invite' : 'Send invite'}
                  </Btn>
                ) : (
                  <Btn
                    size="sm"
                    variant="info"
                    onClick={() => setResendConfirm('login')}
                    loading={resendLoginMutation.isPending}
                  >
                    Resend login link
                  </Btn>
                )}
                {questionnaires.some(isManualSendable) && (
                  <Btn size="sm" variant="info" onClick={() => setSendQOpen(true)}>
                    Send questionnaire
                  </Btn>
                )}
                {pendingChangesCount > 0 && (
                  <Btn
                    size="sm"
                    variant="info"
                    onClick={() => notifyChangesMutation.mutate()}
                    loading={notifyChangesMutation.isPending}
                  >
                    Notify vendor of changes ({pendingChangesCount})
                  </Btn>
                )}
              </>
            )}
            {company.invited_at && (
              <p className="w-full text-right text-xs text-slate-500">
                Last invite sent {formatDate(company.invited_at)}
              </p>
            )}
          </div>
        </div>
      </div>
      {notifyChangesMutation.error && <ErrorMessage error={notifyChangesMutation.error} />}

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Tabs
          tabs={tabItems}
          active={activeKey}
          onSelect={setActiveTab}
          ariaLabel="Vendor review sections"
          className="px-2 pt-2"
        />
        <div className="p-6">
          {activeGroup ? (
            <QuestionnaireTabPanel
              key={activeGroup.questionnaire.id}
              group={activeGroup}
              invitedAt={company.invited_at}
              visibleIds={progress.visibleIds}
              completionByQuestion={progress.completionByQuestion}
              subByQ={subByQ}
              companyId={id}
              companyName={company.company_name}
              vendorNumber={company.vendor_number}
              documentName={documentName}
              documents={documents}
              questionById={questionById}
            />
          ) : activeKey === COMPLIANCE_DOCS_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${COMPLIANCE_DOCS_TAB}`}
              aria-labelledby={`tab-${COMPLIANCE_DOCS_TAB}`}
            >
              <TabHeading
                title="Compliance Documents"
                subtitle="Every document this vendor needs to provide, across all questionnaires."
                actions={
                  mayWrite && tabModel.allComplianceDocs.length > 0 ? (
                    <Btn size="sm" variant="info" onClick={() => setUploadDocOpen(true)}>
                      <NavIcon name="file-text" size={16} />
                      Upload document
                    </Btn>
                  ) : undefined
                }
              />
              {mayWrite && uploadDocOpen && (
                <AdminUploadDocumentModal
                  companyId={id}
                  rows={tabModel.allComplianceDocs}
                  onClose={() => setUploadDocOpen(false)}
                  onUploaded={() => {
                    setUploadDocOpen(false);
                    invalidateCompany(qc, id);
                  }}
                />
              )}
              {tabModel.complianceDocs.length > 0 ? (
                <>
                  <ComplianceDocStats
                    submissions={tabModel.complianceDocs.map((r) => subByQ.get(r.question.id))}
                  />
                  <DocumentReviewTable
                    rows={tabModel.complianceDocs}
                    subByQ={subByQ}
                    companyId={id}
                    documents={documents}
                    documentName={documentName}
                    questionById={questionById}
                  />
                </>
              ) : tabModel.allComplianceDocs.length > 0 ? (
                <p className="text-sm italic text-slate-400">
                  No documents have been marked compliant yet. Documents awaiting review appear
                  under their questionnaire tab.
                </p>
              ) : (
                <p className="text-sm italic text-slate-400">
                  No document requirements for this vendor.
                </p>
              )}
            </div>
          ) : activeKey === ACTIVITY_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${ACTIVITY_TAB}`}
              aria-labelledby={`tab-${ACTIVITY_TAB}`}
            >
              <TabHeading
                title="Activity log"
                subtitle="Everything that has happened to this vendor — admin actions, vendor submissions, and system events."
              />
              <CompanyActivityTab companyId={id} />
            </div>
          ) : activeKey === QUESTIONNAIRES_TAB ? (
            <div
              role="tabpanel"
              id={`tabpanel-${QUESTIONNAIRES_TAB}`}
              aria-labelledby={`tab-${QUESTIONNAIRES_TAB}`}
            >
              {selectedGroupedId === ADDITIONAL_TAB ? (
                <div>
                  <TabHeading
                    title="Additional questions"
                    subtitle="Questions whose category no longer exists."
                  />
                  {/* -mx-4 offsets the rows' own px-4 so text keeps its
                      alignment while highlight bands bleed slightly wider. */}
                  <div className="-mx-4 divide-y divide-slate-400">
                    {tabModel.orphanQuestions.map((question) => (
                      <SubmissionRow
                        key={question.id}
                        question={question}
                        submission={subByQ.get(question.id)}
                        documentName={documentName}
                        companyId={id}
                        documents={documents}
                        instanceOption={instanceOptionLabel(question, questionById)}
                      />
                    ))}
                  </div>
                </div>
              ) : selectedGroupedGroup ? (
                <div>
                  {/* Questionnaire name + review pill as direct flex siblings so
                      the pill centers on the title (margin-box centering issue —
                      see QuestionnaireTabPanel). The panel-level "Questionnaires"
                      heading is intentionally gone: the dropdown already names
                      this tab and the selected questionnaire heads its content. */}
                  <div className="mb-5 flex flex-wrap items-center gap-3">
                    <h2 className="text-xl font-semibold text-slate-900">
                      {selectedGroupedGroup.questionnaire.name}
                    </h2>
                    <QuestionnaireReviewPill
                      group={selectedGroupedGroup}
                      subByQ={subByQ}
                      visibleIds={progress.visibleIds}
                    />
                    <div className="ml-auto">
                      <ExportQuestionnaireButton
                        companyId={id}
                        companyName={company.company_name}
                        vendorNumber={company.vendor_number}
                        group={selectedGroupedGroup}
                        subByQ={subByQ}
                        visibleIds={progress.visibleIds}
                        completionByQuestion={progress.completionByQuestion}
                      />
                    </div>
                  </div>
                  <QuestionnaireStatusStrip
                    progress={questionnaireProgress(
                      selectedGroupedGroup,
                      progress.visibleIds,
                      subByQ,
                      progress.completionByQuestion,
                    )}
                    review={reviewProgress(selectedGroupedGroup, progress.visibleIds, subByQ)}
                    dateLabel="Sent"
                    dateValue={selectedGroupedGroup.questionnaire.sent_at}
                  />
                  <CategorySections
                    sections={selectedGroupedGroup.sections}
                    subByQ={subByQ}
                    companyId={id}
                    documentName={documentName}
                    documents={documents}
                    questionById={questionById}
                  />
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  Select a questionnaire from the Questionnaires tab dropdown above to view its
                  questions.
                </p>
              )}
            </div>
          ) : (
            <div
              role="tabpanel"
              id={`tabpanel-${COMPANY_INFO_TAB}`}
              aria-labelledby={`tab-${COMPANY_INFO_TAB}`}
            >
              <CompanyInfoPanel company={company} />
            </div>
          )}
        </div>
      </div>

      {/* Deactivate/reactivate is a vendor-record action, so it belongs only
          under the Vendor information tab — not beneath questionnaires,
          documents or the activity log. Gated on the resolved activeKey so the
          stale-tab fallback to Vendor information shows it too. */}
      {mayWrite && activeKey === COMPANY_INFO_TAB && (
        <div className="border-t border-slate-200 pt-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-red-600">
            Danger zone
          </h3>
          {isDeactivated ? (
            <div className="mt-3 flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm text-slate-700">
                This vendor is deactivated. Reactivate to restore their previous status and portal
                access.
              </div>
              <Btn onClick={() => setConfirmOpen(true)} loading={reactivateMutation.isPending}>
                Reactivate vendor
              </Btn>
            </div>
          ) : (
            <div className="mt-3 flex items-center justify-between rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="text-sm text-red-700">
                Deactivate this vendor. They&apos;ll be hidden from the vendors list and lose portal
                access. Records are kept — you can reactivate them anytime.
              </div>
              <Btn
                variant="danger"
                onClick={() => setConfirmOpen(true)}
                loading={deactivateMutation.isPending}
              >
                Deactivate vendor
              </Btn>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={confirmOpen}
        title={isDeactivated ? 'Reactivate vendor' : 'Deactivate vendor'}
        confirmLabel={isDeactivated ? 'Reactivate' : 'Deactivate'}
        danger={!isDeactivated}
        loading={isDeactivated ? reactivateMutation.isPending : deactivateMutation.isPending}
        onConfirm={() =>
          isDeactivated ? reactivateMutation.mutate() : deactivateMutation.mutate()
        }
        onClose={() => {
          setConfirmOpen(false);
          deactivateMutation.reset();
          reactivateMutation.reset();
        }}
      >
        {isDeactivated ? (
          <>
            Are you sure you want to reactivate{' '}
            <span className="font-semibold">{company.company_name}</span>? They&apos;ll return to
            their previous status and regain portal access.
          </>
        ) : (
          <>
            Are you sure you want to deactivate{' '}
            <span className="font-semibold">{company.company_name}</span>? They&apos;ll be removed
            from the vendors list and lose portal access. Their records are kept — you can
            reactivate them anytime.
          </>
        )}
        {(deactivateMutation.error ?? reactivateMutation.error) != null && (
          <div className="mt-3">
            <ErrorMessage error={deactivateMutation.error ?? reactivateMutation.error} />
          </div>
        )}
      </ConfirmModal>

      {/* Task 11: confirm before sending invite / login-link email. */}
      {mfaResetConfirm && (
        <ConfirmModal
          open
          title="Reset two-factor authentication"
          confirmLabel="Reset"
          loading={mfaResetMutation.isPending}
          onConfirm={() =>
            mfaResetMutation.mutate(undefined, { onSettled: () => setMfaResetConfirm(false) })
          }
          onClose={() => setMfaResetConfirm(false)}
        >
          Reset two-factor authentication for{' '}
          <span className="font-semibold">{company.company_name}</span>? They will set it up again
          at their next sign-in.
        </ConfirmModal>
      )}

      {resendConfirm && (
        <ConfirmModal
          open
          title={
            resendConfirm === 'invite'
              ? company.invited_at
                ? 'Resend invite'
                : 'Send invite'
              : 'Resend login link'
          }
          confirmLabel="Send"
          loading={inviteMutation.isPending || resendLoginMutation.isPending}
          onConfirm={() => {
            const settle = { onSettled: () => setResendConfirm(null) };
            if (resendConfirm === 'invite') inviteMutation.mutate(undefined, settle);
            else resendLoginMutation.mutate(undefined, settle);
          }}
          onClose={() => setResendConfirm(null)}
        >
          Send a {resendConfirm === 'invite' ? 'new invitation' : 'login link'} email to{' '}
          <span className="font-semibold">{company.email}</span>?
        </ConfirmModal>
      )}

      {sendQOpen && (
        <SendQuestionnaireModal
          companyId={id}
          options={sendOptions}
          onClose={() => setSendQOpen(false)}
          onSent={() => {
            setSendQOpen(false);
            void qc.invalidateQueries({ queryKey: ['company', id, 'questionnaires'] });
          }}
        />
      )}

      <Modal
        open={sentModal !== null}
        onClose={() => setSentModal(null)}
        title={sentModal === 'login_link' ? 'Login link sent' : 'Invitation sent'}
        size="sm"
        footer={
          <Btn variant="info" onClick={() => setSentModal(null)}>
            Done
          </Btn>
        }
      >
        <p className="text-sm text-slate-600">
          {sentModal === 'login_link'
            ? `A login link email has been sent to ${company.email}.`
            : `An invitation email has been sent to ${company.email}.`}
        </p>
        {sentModal === 'invite' && company.invited_at && (
          <p className="mt-2 text-xs text-slate-500">
            Last invite sent {formatDate(company.invited_at)}
          </p>
        )}
      </Modal>
    </div>
  );
}

// Heading shown at the top of each tab panel. The Company information tab also
// passes a subtitle and panel-level actions (Edit / Save / Cancel); category
// tabs pass the category name as the title only.
function TabHeading({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

// One review row: a type tile, the question with its answer/files, then the
// status and per-item actions on the right. The request-changes form (comment
// + reference doc) lives in a modal.
function SubmissionRow({
  question,
  submission,
  documentName,
  companyId,
  documents,
  instanceOption,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
  companyId: string;
  documents: CompanyDocument[];
  instanceOption: string | null;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Read locally rather than threaded: this row renders from three call sites,
  // and a prop would be three chances to forget one. History stays open to
  // every tier -- it is a read.
  const { user: rowUser } = useAdminAuth();
  const rowMayWrite = canWrite(rowUser?.role);

  function closeReject() {
    setRejectOpen(false);
    reviewMutation.reset();
  }

  function closeComment() {
    setCommentOpen(false);
    reviewMutation.reset();
  }

  const reviewMutation = useReviewMutation(companyId, submission?.id, () => {
    closeReject();
    closeComment();
  });

  // Frozen renewal record: once a vendor replaces an APPROVED compliance
  // document, this questionnaire row permanently shows the originally-approved
  // file(s) + an Approved badge and stops being a review surface. The
  // replacement is uploaded, reviewed, and notified exclusively in the
  // Compliance Documents tab (DocumentReviewTable).
  const frozen = frozenApprovedDoc(submission, question.include_in_compliance_documents);
  const files = frozen.frozen ? frozen.files : (submission?.submission_files ?? []);
  // The thread's approval cut points, judged on the LIVE status (both modals).
  const approvals = adminDocumentsTabApprovals(question, submission);

  // Yellow "needs review" band: an answered question whose next move is the
  // admin's (same isAwaitingAdmin signal as the review bar and the pill), so a
  // reviewer can scan straight to the rows that still need a decision. Never
  // shown on a frozen row — its renewal is reviewed in the Compliance
  // Documents tab, not here — and never on a compliance-exempt row, which
  // offers no verdict controls at all, so highlighting it as review work
  // contradicted its own "no review needed" label.
  const needsReview =
    submission !== undefined &&
    !frozen.frozen &&
    !question.exclude_from_compliance &&
    isAwaitingAdmin(submission);

  // The history modal shows uploaded-file versions + policy-document choices, so
  // only offer it for questions that can carry that history — document/adoption
  // questions (even when all their files were later removed and none are live),
  // never a plain text/select answer.
  const canHaveDocHistory =
    question.type === 'document_upload' ||
    question.type === 'multiselect_uploads' ||
    question.type === 'document_adopt' ||
    question.requires_upload ||
    question.adoptable_doc_id != null;
  const hasDocHistory =
    !!submission &&
    (canHaveDocHistory || files.length > 0 || (submission.submission_adoptions?.length ?? 0) > 0);

  // The left bar's colour is `!important`: rows sit in a `divide-y
  // divide-slate-*` list, whose `> * ~ *` rule (higher specificity) recolours
  // every border of every row but the first — so without it only the first
  // needs-review row in a tab kept its orange bar and the rest went grey.
  return (
    <div
      className={`flex items-center gap-3 py-3 ${
        needsReview ? 'border-l-4 !border-l-orange-400 bg-amber-50 pl-3 pr-4' : 'px-4'
      }${question.deleted_at ? ' opacity-60' : ''}`}
    >
      <div className="min-w-0 flex-1">
        {/* No status on a compliance-exempt row: its stored status stays
            'pending' forever because nobody ever reviews it, so the badge read
            "Pending review" directly above a label saying no review is needed. */}
        {submission && !question.exclude_from_compliance && (
          <div className="mb-1">
            <SubmissionStatusBadge
              status={
                frozen.frozen
                  ? frozen.status
                  : effectiveDocStatus(
                      submission.status,
                      hasExpiredLiveFile(submission.submission_files, new Date()),
                    )
              }
            />
          </div>
        )}
        <div className="text-sm font-semibold text-slate-900">
          {question.label}
          {question.deleted_at && (
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-normal text-slate-500">
              Deleted
            </span>
          )}
          {instanceOption && (
            <span className="ml-2 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-normal text-indigo-700">
              For: {instanceOption}
            </span>
          )}
        </div>
        {question.help_text && (
          <div className="mt-0.5 text-xs text-slate-500">{question.help_text}</div>
        )}
        <div className="mt-0.5 flex flex-wrap items-start gap-x-2 gap-y-1 text-sm text-slate-600">
          <AnswerPreview question={question} submission={submission} documentName={documentName} />
          {submission && files.length > 0 && (
            <div className="flex w-full flex-wrap items-start gap-x-2 gap-y-1">
              {files.map((f) => (
                <SubmissionFileItem
                  key={f.id}
                  companyId={companyId}
                  submissionId={submission.id}
                  file={f}
                  questionRequiresExpiry={question.requires_expiry}
                  expiryEditable={!frozen.frozen && rowMayWrite}
                />
              ))}
            </div>
          )}
          {submission &&
            files.map((f) => (
              <FileAnalysisCard
                key={`analysis-${f.id}`}
                companyId={companyId}
                submissionId={submission.id}
                fileId={f.id}
              />
            ))}
        </div>
        {submission?.reviewer_comment && (
          <div className="mt-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
            <div className="font-medium">Reviewer note</div>
            <div>{submission.reviewer_comment}</div>
          </div>
        )}
        {/* Approve/Disapprove errors show here; reject errors show inside the modal. */}
        {!rejectOpen && !commentOpen && reviewMutation.error && (
          <div className="mt-2">
            <ErrorMessage error={reviewMutation.error} />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {hasDocHistory && (
          <Btn
            size="sm"
            variant="secondary"
            aria-label="Document history"
            title="History"
            onClick={() => setHistoryOpen(true)}
          >
            <NavIcon name="clock" size={16} />
          </Btn>
        )}
        {question.exclude_from_compliance ? (
          <span className="text-xs text-slate-500">Not part of compliance — no review needed.</span>
        ) : frozen.frozen ? (
          // Frozen renewal record: no verdict or comment controls here — this
          // row is a permanent snapshot of the original approved answer, and
          // must not become a second review surface for the replacement.
          <span
            className="max-w-56 text-right text-xs text-slate-500"
            title="This row permanently shows the originally approved document. The vendor's replacement is reviewed in the Compliance Documents tab."
          >
            <span className="mr-2 inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-500">
              Approved — original submission
            </span>
            Renewals are managed in the Compliance Documents tab.
          </span>
        ) : optionalUnanswered(question, submission as unknown as Submission) ? (
          // Task 14: the vendor was not obliged to answer and didn't, so there is
          // no decision to make. Offering Compliant / Not Compliant here would
          // invite a verdict on content that does not exist.
          <span className="text-xs text-slate-500">Optional — no answer to review.</span>
        ) : (
          submission &&
          (() => {
            const status = submission.status;
            const isApproved = status === 'approved';
            const isRejected = status === 'rejected';
            const isNa = status === 'not_applicable';
            const awaitingReply = awaitingAdminReply(
              submission,
              question.include_in_compliance_documents,
            );
            // Green count = admin comments the vendor hasn't replied to. When the
            // ball is back in the admin's court (vendor commented last OR
            // resubmitted), reset to 0 so the red "awaiting your reply" dot takes
            // over — the two badges are mutually exclusive.
            const adminComments = awaitingReply
              ? 0
              : (submission.unanswered_admin_comment_count ?? 0);
            const hasComments = adminComments > 0;
            const dec = reviewMutation.variables?.decision;
            const busy = reviewMutation.isPending;
            // Show the spinner on the button that initiated the in-flight call —
            // a reset originates from whichever status is currently active.
            const loadingFor = (target: 'approve' | 'reject' | 'na', active: boolean) =>
              busy && (dec === target || (dec === 'reset' && active));
            // Compliant / Not Compliant / N/A and the comment composer are all
            // writes. A viewer sees the status badge above and nothing else.
            if (!rowMayWrite) return null;
            return (
              <>
                <Btn
                  size="sm"
                  variant={isApproved ? 'success' : 'secondary'}
                  aria-pressed={isApproved}
                  onClick={() =>
                    reviewMutation.mutate({ decision: isApproved ? 'reset' : 'approve' })
                  }
                  loading={loadingFor('approve', isApproved)}
                >
                  <NavIcon name="check-circle" size={16} />
                  Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isRejected ? 'danger' : 'secondary'}
                  aria-pressed={isRejected}
                  onClick={() =>
                    isRejected ? reviewMutation.mutate({ decision: 'reset' }) : setRejectOpen(true)
                  }
                  loading={loadingFor('reject', isRejected)}
                >
                  <NavIcon name="x-circle" size={16} />
                  Not Compliant
                </Btn>
                <Btn
                  size="sm"
                  variant={isNa ? 'warning' : 'secondary'}
                  aria-pressed={isNa}
                  onClick={() => reviewMutation.mutate({ decision: isNa ? 'reset' : 'na' })}
                  loading={loadingFor('na', isNa)}
                >
                  N/A
                </Btn>
                <span className="relative inline-flex">
                  <Btn
                    size="sm"
                    variant={hasComments ? 'info' : 'secondary'}
                    aria-label={
                      awaitingReply
                        ? `Vendor replied — awaiting your reply for "${question.label}"`
                        : hasComments
                          ? `Comment added for "${question.label}" — ${adminComments} so far`
                          : `Add comment for "${question.label}"`
                    }
                    onClick={() => setCommentOpen(true)}
                  >
                    <NavIcon name="edit" size={16} />
                    {hasComments ? 'Comment added' : 'Comments'}
                  </Btn>
                  {hasComments && (
                    <span
                      className="pointer-events-none absolute -left-1.5 -top-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white"
                      aria-hidden="true"
                    >
                      {adminComments}
                    </span>
                  )}
                  {awaitingReply && (
                    <span
                      className="pointer-events-none absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                      aria-hidden="true"
                    />
                  )}
                </span>
              </>
            );
          })()
        )}
      </div>

      {rowMayWrite && rejectOpen && (
        <NotCompliantModal
          question={question}
          documents={documents}
          pending={reviewMutation.isPending}
          error={reviewMutation.error}
          onClose={closeReject}
          onSubmit={(payload) => reviewMutation.mutate({ decision: 'reject', ...payload })}
        />
      )}

      {rowMayWrite && commentOpen && (
        <CommentModal
          question={question}
          companyId={companyId}
          submissionId={submission?.id}
          submission={submission}
          onClose={closeComment}
          scope="questionnaire"
          approvals={approvals}
        />
      )}

      {historyOpen && submission && (
        <FileHistoryModal
          companyId={companyId}
          submissionId={submission.id}
          questionLabel={question.label}
          approvals={approvals}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </div>
  );
}

// All questions of one questionnaire group, used for tab badges. Doc questions
// are included — since PR #113 they stay inline in their category sections and
// are only mirrored (never moved) into the Compliance Documents tab.
function groupQuestions(g: QuestionnaireGroup): Question[] {
  return g.sections.flatMap((s) => s.questions);
}

// The admin's per-questionnaire review pill: derived from this questionnaire's
// submissions (status + who commented last). null when there is nothing to show.
// Exported for questionnaireReviewPill.test.tsx — it takes no hooks or queries,
// so it renders standalone.
export function QuestionnaireReviewPill({
  group,
  subByQ,
  visibleIds,
}: {
  group: QuestionnaireGroup;
  subByQ: Map<string, AdminSubmission>;
  visibleIds: Set<string>;
}) {
  // Compliance-exempt and hidden questions are excluded: they are never
  // reviewed, so their submissions stay 'pending' and used to pin this pill at
  // "Action required" even once every reviewable item was approved.
  const subs = reviewableSubmissions(groupQuestions(group), subByQ, visibleIds);
  const label = ADMIN_REVIEW_LABEL[reviewTurn(subs)];
  if (!label) return null;
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
      {label}
    </span>
  );
}

// Progress + date status strip shown above a questionnaire's questions. Shared
// by the own-tab panel (date = company "Invited") and the consolidated
// Questionnaires tab (date = this questionnaire's "Sent" time), so both read
// identically. dateValue is an ISO string or null (rendered as "—").
// One card pairs the vendor's completion bar with the admin's own review bar
// ("N reviewed · M need review") so both sides of the workflow read together.
function QuestionnaireStatusStrip({
  progress,
  review,
  dateLabel,
  dateValue,
}: {
  progress: QuestionnaireProgress;
  review: ReviewProgress;
  dateLabel: string;
  dateValue: string | null;
}) {
  return (
    <div className="mb-5 grid gap-4 lg:grid-cols-3">
      <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-2 sm:divide-x sm:divide-slate-100 lg:col-span-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Vendor progress
          </div>
          <div className="mt-2">
            <ProgressBar value={progress.percent} />
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {progress.completed} / {progress.total} complete
          </div>
        </div>
        <div className="sm:pl-4">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-500 sm:text-right">
            Your review
          </div>
          <div className="mt-2">
            <ProgressBar value={review.percent} color="#16a34a" />
          </div>
          <div className="mt-1 text-xs text-slate-500">
            <span className="font-semibold text-green-700">{review.reviewed}</span> reviewed ·{' '}
            <span className="font-semibold text-slate-700">{review.needReview}</span> need review
          </div>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
          {dateLabel}
        </div>
        <div className="mt-2 text-sm text-slate-700">
          {dateValue ? new Date(dateValue).toLocaleString() : '—'}
        </div>
      </div>
    </div>
  );
}

// Category sections for one questionnaire tab: one bordered card per category
// with a tinted header band (same idiom as the Compliance Documents table
// header), then the standard review rows. Compliance-flagged questions render here too
// while their submission is unapproved; once approved they move to the
// Compliance Documents tab.
function CategorySections({
  sections,
  subByQ,
  companyId,
  documentName,
  documents,
  questionById,
  categoryHeadingLevel: CategoryHeading = 'h3',
}: {
  sections: CategorySection[];
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  documentName: (id: string) => string;
  documents: CompanyDocument[];
  questionById: Map<string, Question>;
  // 'h4' when nested under a per-questionnaire heading (Questionnaires tab).
  categoryHeadingLevel?: 'h3' | 'h4';
}) {
  return (
    <div className="space-y-4">
      {sections.map((s) => (
        <section key={s.category.id} className="overflow-hidden rounded-lg border border-slate-200">
          <CategoryHeading className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
            {s.category.name}
          </CategoryHeading>
          {/* Rows carry their own px-4 so a needs-review row's amber band can
              run edge-to-edge inside the card. */}
          <div className="divide-y divide-slate-400">
            {s.questions.map((question) => (
              <SubmissionRow
                key={question.id}
                question={question}
                submission={subByQ.get(question.id)}
                documentName={documentName}
                companyId={companyId}
                documents={documents}
                instanceOption={instanceOptionLabel(question, questionById)}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// One applicable questionnaire's tab panel. Leads with a per-questionnaire
// Progress + Invited header block (counting this questionnaire's visible
// questions, mirroring the vendor view), then presents the questionnaire's categories as a
// horizontal sub-tab bar with the selected category's review rows below —
// replacing the older stacked-cards layout. Sub-tab keys are namespaced with a
// "cat:" prefix so their generated tab/tabpanel ids never collide with the
// outer questionnaire tabs.
function QuestionnaireTabPanel({
  group,
  invitedAt,
  visibleIds,
  completionByQuestion,
  subByQ,
  companyId,
  companyName,
  vendorNumber,
  documentName,
  documents,
  questionById,
}: {
  group: QuestionnaireGroup;
  invitedAt: string | null;
  visibleIds: Set<string>;
  completionByQuestion: Map<string, CompletionReason | null>;
  subByQ: Map<string, AdminSubmission>;
  companyId: string;
  companyName: string;
  vendorNumber: string | null;
  documentName: (id: string) => string;
  documents: CompanyDocument[];
  questionById: Map<string, Question>;
}) {
  const prog = questionnaireProgress(group, visibleIds, subByQ, completionByQuestion);
  const review = reviewProgress(group, visibleIds, subByQ);

  const subTabKey = (categoryId: string) => `cat:${categoryId}`;
  const subTabs: TabItem[] = group.sections.map((s) => ({
    key: subTabKey(s.category.id),
    label: s.category.name,
    badge: outstandingActionCount(s.questions, completionByQuestion, subByQ, { skipFrozen: true }),
  }));

  // Default to the first category; reset when switching questionnaires (a new
  // group remounts this component because activeGroup keys by questionnaire.id
  // via the parent's render — but guard anyway against a stale key).
  const [activeSub, setActiveSub] = useState<string>(subTabs[0]?.key ?? '');
  const activeKey = subTabs.some((t) => t.key === activeSub) ? activeSub : (subTabs[0]?.key ?? '');
  const activeSection = group.sections.find((s) => subTabKey(s.category.id) === activeKey);

  return (
    <div
      role="tabpanel"
      id={`tabpanel-${group.questionnaire.id}`}
      aria-labelledby={`tab-${group.questionnaire.id}`}
    >
      {/* Title and review pill as direct flex siblings so the pill centers on
          the title text. TabHeading carries its own bottom margin, so nesting it
          here would make the pill center on its (taller) margin box and drift
          below the title. */}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold text-slate-900">{group.questionnaire.name}</h2>
        <QuestionnaireReviewPill group={group} subByQ={subByQ} visibleIds={visibleIds} />
        <div className="ml-auto">
          <ExportQuestionnaireButton
            companyId={companyId}
            companyName={companyName}
            vendorNumber={vendorNumber}
            group={group}
            subByQ={subByQ}
            visibleIds={visibleIds}
            completionByQuestion={completionByQuestion}
          />
        </div>
      </div>

      {/* Per-questionnaire status strip: progress scoped to this
          questionnaire's visible questions (vendor-view parity), plus the
          (shared) invite timestamp. */}
      <QuestionnaireStatusStrip
        progress={prog}
        review={review}
        dateLabel="Invited"
        dateValue={invitedAt}
      />

      {/* Categories as sub-tabs (one active category panel at a time). */}
      <Tabs
        tabs={subTabs}
        active={activeKey}
        onSelect={setActiveSub}
        ariaLabel={`${group.questionnaire.name} categories`}
        className="border-slate-400"
      />
      {activeSection && (
        <div
          role="tabpanel"
          id={`tabpanel-${activeKey}`}
          aria-labelledby={`tab-${activeKey}`}
          className="pt-4"
        >
          {/* -mx-4 offsets the rows' own px-4 (see CategorySections). */}
          <div className="-mx-4 divide-y divide-slate-400">
            {activeSection.questions.map((question) => (
              <SubmissionRow
                key={question.id}
                question={question}
                submission={subByQ.get(question.id)}
                documentName={documentName}
                companyId={companyId}
                documents={documents}
                instanceOption={instanceOptionLabel(question, questionById)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Admin "upload on behalf of vendor" modal for the Compliance Documents tab.
// Requires picking one of the vendor's compliance-doc questions and a file,
// then runs the standard 3-step upload (presign → PUT → register) against the
// admin endpoints and lets the caller invalidate the page queries so the new
// file appears in the review table.
function AdminUploadDocumentModal({
  companyId,
  rows,
  onClose,
  onUploaded,
}: {
  companyId: string;
  rows: ComplianceDocRow[];
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [questionId, setQuestionId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [expiry, setExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // Group the question options by category name for a scannable dropdown; the
  // rows already arrive in questionnaire → category → question order.
  const groups: { categoryName: string; questions: Question[] }[] = [];
  for (const { question, categoryName } of rows) {
    const last = groups[groups.length - 1];
    if (last && last.categoryName === categoryName) last.questions.push(question);
    else groups.push({ categoryName, questions: [question] });
  }

  // Whether an expiry date is required is a property of the CHOSEN question, so
  // nothing is asked for until one is picked. Undefined before then.
  const selected = rows.find((r) => r.question.id === questionId)?.question;
  const requiresExpiry = selected?.requires_expiry ?? false;
  const missingExpiry = requiresExpiry && !expiry;

  // Switching question drops any date already typed: it was entered against the
  // previous question's requirement, and silently carrying it over would attach
  // an unrelated expiry to the new document.
  function selectQuestion(id: string) {
    setQuestionId(id);
    setExpiry('');
  }

  async function submit() {
    if (!questionId || !file || uploading || missingExpiry) return;
    setUploading(true);
    setError(null);
    try {
      const mimeType = file.type || 'application/octet-stream';
      const presigned = await adminApi.post<{ path: string; url: string }>(
        `/api/companies/${companyId}/submissions/${questionId}/upload-url`,
        { file_name: file.name, mime_type: mimeType },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      await adminApi.post(`/api/companies/${companyId}/submissions/${questionId}/files`, {
        file_path: presigned.path,
        file_name: file.name,
        size_bytes: file.size,
        mime_type: mimeType,
        expires_at: fromDateInput(expiry),
      });
      onUploaded();
    } catch (err) {
      setError(err);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open
      title="Upload document on behalf of vendor"
      size="md"
      onClose={onClose}
      footer={
        <>
          <Btn size="sm" variant="ghost" onClick={onClose} disabled={uploading}>
            Cancel
          </Btn>
          <Btn
            size="sm"
            onClick={() => void submit()}
            disabled={!questionId || !file || missingExpiry}
            loading={uploading}
          >
            Upload
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Related document requirement" required>
          <Select
            value={questionId}
            onChange={(e) => selectQuestion(e.target.value)}
            disabled={uploading}
          >
            <option value="">— Select a document —</option>
            {groups.map((g) => (
              <optgroup key={g.categoryName} label={g.categoryName}>
                {g.questions.map((q) => (
                  <option key={q.id} value={q.id}>
                    {complianceDocName(q)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Field>
        <Field label="File" required>
          <FileDrop
            multiple={false}
            accept={DOCUMENT_ACCEPT}
            disabled={uploading}
            onFiles={(list) => {
              const f = list[0];
              if (f) setFile(f);
            }}
            hint={file ? `Selected: ${file.name}` : uploading ? 'Uploading…' : undefined}
          />
        </Field>
        {/* Driven by the chosen question: hidden entirely until one is selected
            (the requirement is unknowable before then) and on questions that
            don't expire, required on the ones that do. */}
        {requiresExpiry && (
          <Field label="Expiry date" required>
            <DateField
              value={expiry}
              onChange={setExpiry}
              disabled={uploading}
              ariaLabel="Expiry date"
              placeholder="Expiry date required"
              invalid={missingExpiry && !!file}
            />
            <span
              className={`mt-1 block text-xs ${
                missingExpiry && file ? 'font-medium text-red-600' : 'text-slate-400'
              }`}
            >
              This document requires an expiry date.
            </span>
          </Field>
        )}
        {error != null && <ErrorMessage error={error} />}
      </div>
    </Modal>
  );
}

// One submitted file shown as an inline link. Images open in a modal lightbox;
// other file types open in a new tab. The signed URL is fetched on demand.
function SubmissionFileItem({
  companyId,
  submissionId,
  file,
  questionRequiresExpiry,
  expiryEditable,
}: {
  companyId: string;
  submissionId: string;
  file: SubmissionFile;
  /** Mirrors the server rule: hides Clear on requires_expiry questions. */
  questionRequiresExpiry: boolean;
  /** False on frozen approved rows, whose files are a historical record. */
  expiryEditable: boolean;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isImage = file.mime_type.startsWith('image/');
  const downloadPath = `/api/companies/${companyId}/submissions/${submissionId}/files/${file.id}/download-url`;

  // No thumbnail to populate, so the short-lived signed URL is only fetched when
  // the lightbox is actually opened.
  const urlQuery = useQuery({
    queryKey: ['submission-file-url', submissionId, file.id],
    queryFn: () => adminApi.get<{ url: string }>(downloadPath),
    enabled: isImage && lightboxOpen,
    staleTime: 4 * 60 * 1000,
  });

  async function openInNewTab() {
    const url =
      urlQuery.data?.url ??
      (await adminApi.get<{ url: string }>(downloadPath).catch(() => null))?.url;
    if (url) window.open(url, '_blank', 'noopener');
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => (isImage ? setLightboxOpen(true) : void openInNewTab())}
        className="inline-flex items-center gap-1 text-xs text-[#4D148C] hover:underline"
      >
        <span aria-hidden="true">📎</span>
        {file.file_name}
      </button>
      <FileExpiryControl
        companyId={companyId}
        submissionId={submissionId}
        file={file}
        requiresExpiry={questionRequiresExpiry}
        editable={expiryEditable}
      />

      {isImage && lightboxOpen && (
        <Modal
          open
          title={file.file_name}
          size="lg"
          onClose={() => setLightboxOpen(false)}
          footer={
            <>
              <Btn
                variant="ghost"
                size="sm"
                disabled={!urlQuery.data?.url}
                onClick={() =>
                  urlQuery.data?.url && downloadSignedUrl(urlQuery.data.url, file.file_name)
                }
              >
                Download file
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => void openInNewTab()}>
                Open in new tab
              </Btn>
              <Btn variant="secondary" size="sm" onClick={() => setLightboxOpen(false)}>
                Close
              </Btn>
            </>
          }
        >
          {urlQuery.data?.url ? (
            <img
              src={urlQuery.data.url}
              alt={file.file_name}
              className="mx-auto max-h-[65vh] max-w-full rounded-md"
            />
          ) : (
            <div className="flex h-40 items-center justify-center text-sm text-slate-400">
              {urlQuery.isError ? 'Image unavailable' : 'Loading…'}
            </div>
          )}
        </Modal>
      )}
    </span>
  );
}

export function AnswerPreview({
  question,
  submission,
  documentName,
}: {
  question: Question;
  submission: AdminSubmission | undefined;
  documentName: (id: string) => string;
}) {
  // Document questions are satisfied by an upload or an adoption, not a typed
  // answer, so they must never fall through to a generic "Not answered yet".
  if (question.type === 'document_adopt' || question.type === 'document_upload') {
    const adoptions = submission?.submission_adoptions ?? [];
    if (adoptions.length > 0) {
      return (
        <ul className="space-y-1">
          {adoptions.map((ad) => (
            <li key={ad.id} className="text-sm">
              {ad.mode === 'adopted' ? '✓ Adopted: ' : '↑ Uploaded: '}
              {documentName(ad.document_id)}
            </li>
          ))}
        </ul>
      );
    }
    if (question.type === 'document_upload') {
      // Uploaded file(s) render below this preview with their expiry chip.
      if ((submission?.submission_files?.length ?? 0) > 0) return null;
      // Task 14: an OPTIONAL document the vendor chose not to provide isn't
      // "awaited" — nothing is outstanding, so say nothing.
      if (!question.required) return null;
      return <span className="italic text-slate-400">Awaiting document</span>;
    }
    return <span className="italic text-slate-400">No adoption recorded</span>;
  }

  if (!submission) return <span className="italic text-slate-400">Not answered yet</span>;
  if (submission.in_revision) {
    return <span className="italic text-amber-600">Vendor is revising this answer</span>;
  }
  const a = submission.answer;
  if (question.type === 'signature') {
    if (
      a &&
      typeof a === 'object' &&
      !Array.isArray(a) &&
      typeof (a as { signature_data_url?: unknown }).signature_data_url === 'string'
    ) {
      const sig = a as { signature_data_url: string; signer_name: string; signed_at: string };
      return (
        <div className="space-y-1">
          <img
            src={sig.signature_data_url}
            alt="Signature"
            className="h-24 rounded border border-slate-200 bg-white object-contain"
          />
          <div className="text-sm text-slate-700">
            Signed by <span className="font-medium">{sig.signer_name}</span>
          </div>
          <div className="text-xs text-slate-500">{new Date(sig.signed_at).toLocaleString()}</div>
        </div>
      );
    }
    return <span className="italic text-slate-400">Not signed yet</span>;
  }
  const pills = answerOptionPills(question, a);
  if (pills) return <OptionPills pills={pills} />;
  if (Array.isArray(a)) return <VendorAnswerBox>{(a as string[]).join(', ')}</VendorAnswerBox>;
  if (typeof a === 'boolean') return <VendorAnswerBox>{a ? 'Yes' : 'No'}</VendorAnswerBox>;
  if (a === null || a === undefined || a === '') {
    return <span className="italic text-slate-400">Not answered yet</span>;
  }
  return <VendorAnswerBox>{String(a)}</VendorAnswerBox>;
}

// A typed answer sat in grey text directly under the equally grey help text,
// so reviewers couldn't tell which line the vendor wrote. The box carries the
// same brand-accent tint as a selected OptionPill, so every vendor answer —
// typed or picked — reads in one colour. There is no visible caption; the
// sr-only prefix tells a screen reader which line is the answer.
function VendorAnswerBox({ children }: { children: ReactNode }) {
  return (
    <div
      className="mt-1 w-full break-words rounded-md border px-2.5 py-1.5 text-sm text-slate-900"
      style={{
        borderColor: 'color-mix(in srgb, var(--brand-accent) 30%, white)',
        backgroundColor: 'color-mix(in srgb, var(--brand-accent) 6%, white)',
      }}
    >
      <span className="sr-only">Vendor response: </span>
      {children}
    </div>
  );
}

// Read-only pill list for option-based questions: every offered option on its
// own row, with the company's selection(s) tinted in the brand accent (same
// idiom as StatusFilterTabs). Selection is also conveyed by the check glyph
// and sr-only text, not colour alone.
function OptionPills({ pills }: { pills: AnswerOptionPill[] }) {
  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      {pills.map((p, i) => (
        <span
          key={i}
          className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
            p.selected ? '' : 'border-slate-200 bg-white text-slate-500'
          }`}
          style={
            p.selected
              ? {
                  color: 'var(--brand-accent)',
                  borderColor: 'var(--brand-accent)',
                  backgroundColor: 'color-mix(in srgb, var(--brand-accent) 12%, white)',
                }
              : undefined
          }
        >
          {p.selected && <span aria-hidden="true">✓</span>}
          {p.label}
          {p.selected && <span className="sr-only">(selected)</span>}
        </span>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Company information panel. Read-only by default; the panel-level Edit
// button beside the tab heading switches both cards — FedEx Details and
// Vendor Details — to inputs, saved together through a single PATCH.
// ----------------------------------------------------------------------

type CompanyInfoForm = {
  // Trading name (companies.company_name) — the vendor's identity string.
  // Kept OUT of infoTextFields: the empty→null collapse would send null, which
  // CompanyUpdateInput rejects (min(1), not nullable). See the save mutation.
  company_name: string;
  contractor_types: string[];
  vendor_number: string;
  territory: string;
  contract_numbers: string[];
  cluster_regions: string[];
  stations: string[];
  abn: string;
  legal_entity_name: string;
  address_line: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
  trustee_info: string;
  primary_contact_name: string;
  contact_role: string;
  contact_phone: string;
  contact_email: string;
  employee_count: string;
  // Admin-only internal note (0073). Rendered last in the panel — a multi-line
  // block wedged between short fields reads badly.
  notes: string;
};

// Every field except employee_count and the list fields is sent as trimmed
// text (empty → null); employee_count is parsed to an integer and the lists are
// sent as arrays separately in the save mutation (empty → null, except
// cluster_regions, which must never be empty).
const infoTextFields = [
  'vendor_number',
  'territory',
  'abn',
  'legal_entity_name',
  'address_line',
  'city',
  'state',
  'postcode',
  'country',
  'trustee_info',
  'primary_contact_name',
  'contact_role',
  'contact_phone',
  'contact_email',
  // Membership here is what earns notes the trim and the empty → null collapse.
  'notes',
] as const;

function toCompanyInfoForm(c: Company): CompanyInfoForm {
  return {
    company_name: c.company_name,
    contractor_types: c.contractor_types ?? [],
    vendor_number: c.vendor_number ?? '',
    territory: c.territory ?? '',
    contract_numbers: c.contract_numbers ?? [],
    cluster_regions: c.cluster_regions ?? [],
    stations: c.stations ?? [],
    abn: c.abn ?? '',
    legal_entity_name: c.legal_entity_name ?? '',
    address_line: c.address_line ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    postcode: c.postcode ?? '',
    country: c.country ?? '',
    trustee_info: c.trustee_info ?? '',
    primary_contact_name: c.primary_contact_name ?? '',
    contact_role: c.contact_role ?? '',
    contact_phone: c.contact_phone ?? '',
    contact_email: c.contact_email ?? '',
    employee_count: c.employee_count === null ? '' : String(c.employee_count),
    notes: c.notes ?? '',
  };
}

export function CompanyInfoPanel({
  company,
  initialEditing = false,
}: {
  company: Company;
  /** Test seam: renderToStaticMarkup cannot click Edit, so the parity test
   *  mounts the panel directly in edit mode. Production callers omit it. */
  initialEditing?: boolean;
}) {
  const qc = useQueryClient();
  const { settings } = usePortalSettings();
  const { user } = useAdminAuth();
  const contractorTypes = contractorTypeOptions(settings);
  const clusterRegions = clusterRegionOptions(settings);
  const stations = stationOptions(settings);
  const [form, setForm] = useState<CompanyInfoForm>(() => toCompanyInfoForm(company));
  const [error, setError] = useState<unknown>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Both cards start read-only; the Edit button beside the heading switches
  // in the inputs.
  const [editing, setEditing] = useState(initialEditing);

  useEffect(() => {
    setForm(toCompanyInfoForm(company));
  }, [company.id, company.updated_at]);

  // Dirty when any field differs from the company record's current values.
  const baseline = toCompanyInfoForm(company);
  const sameList = (a: string[], b: string[]) =>
    a.length === b.length && a.every((v, i) => v === b[i]);
  const dirty =
    form.company_name !== baseline.company_name ||
    infoTextFields.some((key) => form[key] !== baseline[key]) ||
    !sameList(form.contractor_types, baseline.contractor_types) ||
    !sameList(form.contract_numbers, baseline.contract_numbers) ||
    !sameList(form.cluster_regions, baseline.cluster_regions) ||
    !sameList(form.stations, baseline.stations) ||
    form.employee_count !== baseline.employee_count;

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, string | string[] | number | null> = {};
      for (const key of infoTextFields) {
        const trimmed = form[key].trim();
        payload[key] = trimmed.length === 0 ? null : trimmed;
      }
      // Trading name is required (min(1) in CompanyUpdateInput) and sits
      // outside infoTextFields so it never collapses to null. Mirrors the
      // employee_count guard below: a sync throw lands in onError.
      const tradingName = form.company_name.trim();
      if (tradingName.length === 0) {
        throw new Error('Trading name is required');
      }
      // Decision C (spec 2026-10-27): a vendor always has at least one region.
      // The server rejects an empty list too; throwing here gives the admin
      // the reason instead of a generic 400. Never send null — a PATCH that
      // omits the field leaves it unchanged, but this panel sends every field.
      if (form.cluster_regions.length === 0) {
        throw new Error('At least one cluster region is required');
      }
      payload.cluster_regions = form.cluster_regions;
      payload.company_name = tradingName;
      // Empty list clears the field (NULL), mirroring the blank-text convention.
      payload.contractor_types = form.contractor_types.length === 0 ? null : form.contractor_types;
      payload.contract_numbers = form.contract_numbers.length === 0 ? null : form.contract_numbers;
      // The server derives the transitional `station` scalar from stations[0].
      payload.stations = form.stations.length === 0 ? null : form.stations;
      // Mirrors the company portal's handling: blank clears the value, anything
      // else must parse to a non-negative integer (sync throws land in onError).
      const empCount = form.employee_count.trim();
      if (empCount === '') {
        payload.employee_count = null;
      } else {
        const n = Number.parseInt(empCount, 10);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error('# of employees must be a non-negative number');
        }
        payload.employee_count = n;
      }
      return adminApi.patch<{ company: Company }>(`/api/companies/${company.id}`, payload);
    },
    onSuccess: () => {
      setError(null);
      setSavedAt(Date.now());
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['company', company.id] });
    },
    onError: (err) => setError(err),
  });

  // On-demand ABN re-check — the button behind the "ABN needs action" badge.
  // A { persisted: false } response means the register gave no definitive
  // answer (outage/throttle); nothing changed, so we prompt a retry rather than
  // surface it as an error. A persisted result refreshes the company so the
  // verdict, status badge, and the Vendor information tab badge all update.
  const [reverifyNote, setReverifyNote] = useState<string | null>(null);
  const reverifyAbnMutation = useMutation({
    mutationFn: () =>
      adminApi.post<{ persisted: boolean; status?: string }>(
        `/api/companies/${company.id}/abn-reverify`,
      ),
    onMutate: () => setReverifyNote(null),
    onSuccess: (res) => {
      if (res.persisted) {
        void qc.invalidateQueries({ queryKey: ['company', company.id] });
        void qc.invalidateQueries({ queryKey: ['companies'] });
      } else {
        setReverifyNote("Couldn't reach the ABN register just now — please try again.");
      }
    },
  });

  const abnCheck = useAbnVerification(editing ? form.abn : '', 'admin');

  function set<K extends keyof CompanyInfoForm>(key: K, value: CompanyInfoForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Auto-fill registered details from a verified ABN. Smart-sync: fills when
  // blank or still holding our own prior fill; never clobbers a manual edit.
  const abnFill = abnCheck.state === 'verified' ? abnCheck.result : null;
  useAutoFillField(abnFill?.entityName ?? null, form.legal_entity_name, (v) =>
    set('legal_entity_name', v),
  );
  useAutoFillField(abnFill?.addressState ?? null, form.state, (v) => set('state', v));
  useAutoFillField(abnFill?.addressPostcode ?? null, form.postcode, (v) => set('postcode', v));

  // Cancel discards in-progress edits, clears any error, and returns to the
  // read-only view.
  function cancelEdit() {
    setForm(toCompanyInfoForm(company));
    setError(null);
    saveMutation.reset();
    setEditing(false);
  }

  // MultiSelect only surfaces the options it's handed and rebuilds the
  // selection from them. Options narrow to the signed-in user's scope, then
  // fold in any values already saved on this vendor — whether retired from
  // Settings or outside the editing admin's scope — so they stay visible and
  // removable instead of being silently dropped on the next save (the 0069
  // fold-in rule, extended to scope). scopedOptions does both in one call.
  //
  // The narrowing argument is the signed-in user's EFFECTIVE assignment, never
  // the stored column: [] and null both mean unrestricted (D15). Scope is
  // orthogonal to tier (D14) -- a super admin's own assigned_* narrows their
  // pickers exactly like an admin's, so effectiveAssignment
  // (../lib/adminAccess.ts) no longer branches on role.
  //
  // The third argument stays RAW on purpose: `form.contractor_types` /
  // `form.stations` are this VENDOR's saved values, a company attribute with no
  // role dimension at all.
  const contractorTypeOptionsWithSaved = scopedOptions(
    contractorTypes,
    effectiveAssignment(user?.assigned_contractor_types),
    form.contractor_types,
  );
  const stationOptionsWithSaved = scopedOptions(
    stations,
    effectiveAssignment(user?.assigned_stations),
    form.stations,
  );
  // Regions have no scope axis (companyFilterOptions), so the scope argument is
  // null = unrestricted; the third argument folds in any saved region Settings
  // no longer lists, so it stays visible and removable (the 0069 rule).
  const clusterRegionOptionsWithSaved = scopedOptions(clusterRegions, null, form.cluster_regions);

  return (
    <div>
      {/* Heading row carries the panel-level controls: Edit when read-only,
          Cancel/Save while editing. One Save writes both cards' fields. */}
      <TabHeading
        title="Vendor information"
        subtitle="FedEx details you manage and information the vendor provided."
        actions={
          editing ? (
            <>
              <Btn
                size="sm"
                variant="secondary"
                onClick={cancelEdit}
                disabled={saveMutation.isPending}
              >
                Cancel
              </Btn>
              <Btn
                size="sm"
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!dirty}
              >
                Save
              </Btn>
            </>
          ) : (
            <>
              {savedAt !== null && <span className="text-xs text-emerald-600">Saved</span>}
              {canWrite(user?.role) && (
                <Btn size="sm" variant="info" onClick={() => setEditing(true)}>
                  Edit
                </Btn>
              )}
            </>
          )
        }
      />

      {error != null && (
        <div className="mb-4">
          <ErrorMessage error={error} />
        </div>
      )}

      <div className="grid items-start gap-4 md:grid-cols-2">
        {/* FedEx Details — admin-managed fields. */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-1 text-base font-semibold text-slate-800">FedEx Details</h3>
          {editing ? (
            <div>
              <InfoRow label={companyInfoLabel(settings, 'contractorType')}>
                {/* Dropdown multi-select matching the create-vendor form. Options
                    include any saved types no longer in Settings so they stay
                    visible and removable rather than being silently dropped. */}
                <MultiSelect
                  label={companyInfoLabel(settings, 'contractorType')}
                  fullWidth
                  values={contractorTypeOptionsWithSaved}
                  selected={form.contractor_types}
                  onChange={(next) => set('contractor_types', next)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(settings, 'vendorNumber')}
                htmlFor="company-vendor-number"
              >
                <TextInput
                  id="company-vendor-number"
                  value={form.vendor_number}
                  onChange={(e) => set('vendor_number', e.target.value)}
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'territory')} htmlFor="company-territory">
                <TextInput
                  id="company-territory"
                  value={form.territory}
                  onChange={(e) => set('territory', e.target.value)}
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'contractNumber')}>
                <StringListEditor
                  value={form.contract_numbers}
                  onChange={(list) => set('contract_numbers', list)}
                  itemLabel="contract number"
                  addLabel="Add contract number"
                  maxLength={255}
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'clusterRegion')}>
                <MultiSelect
                  label={companyInfoLabel(settings, 'clusterRegion')}
                  fullWidth
                  values={clusterRegionOptionsWithSaved}
                  selected={form.cluster_regions}
                  onChange={(next) => set('cluster_regions', next)}
                />
                {clusterRegions.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Add cluster regions in Settings to populate this dropdown.
                  </p>
                )}
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'station')}>
                <MultiSelect
                  label={companyInfoLabel(settings, 'station')}
                  fullWidth
                  values={stationOptionsWithSaved}
                  selected={form.stations}
                  onChange={(next) => set('stations', next)}
                />
                {stations.length === 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    Add stations in Settings to populate this dropdown.
                  </p>
                )}
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(settings, 'notes')}
                htmlFor="company-notes"
                align="start"
              >
                <TextArea
                  id="company-notes"
                  value={form.notes}
                  maxLength={5000}
                  onChange={(e) => set('notes', e.target.value)}
                />
              </InfoRow>
            </div>
          ) : (
            <div>
              <InfoRow label={companyInfoLabel(settings, 'contractorType')}>
                <ReadOnlyValue
                  value={
                    company.contractor_types && company.contractor_types.length > 0
                      ? company.contractor_types.join(', ')
                      : null
                  }
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'vendorNumber')}>
                <ReadOnlyValue value={company.vendor_number} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'territory')}>
                <ReadOnlyValue value={company.territory} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'contractNumber')}>
                <ReadOnlyValue
                  value={
                    company.contract_numbers && company.contract_numbers.length > 0
                      ? company.contract_numbers.join(', ')
                      : null
                  }
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'clusterRegion')}>
                <ReadOnlyValue
                  value={
                    company.cluster_regions && company.cluster_regions.length > 0
                      ? company.cluster_regions.join(', ')
                      : null
                  }
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'station')}>
                <ReadOnlyValue
                  value={
                    company.stations && company.stations.length > 0
                      ? company.stations.join(', ')
                      : null
                  }
                />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'notes')} align="start">
                <ReadOnlyValue value={company.notes ?? null} />
              </InfoRow>
            </div>
          )}
        </div>

        {/* Vendor Details — values the vendor submitted, editable here too so
            an admin can correct them on the vendor's behalf. */}
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <h3 className="mb-1 text-base font-semibold text-slate-800">Vendor Details</h3>
          {editing ? (
            <div>
              <InfoRow label="ABN" htmlFor="company-abn">
                <TextInput
                  id="company-abn"
                  value={form.abn}
                  onChange={(e) => set('abn', e.target.value)}
                />
                <AbnStatusIndicator state={abnCheck.state} result={abnCheck.result} />
              </InfoRow>
              <InfoRow label="Trustee Name and ACN" htmlFor="company-trustee-info">
                <TextInput
                  id="company-trustee-info"
                  value={form.trustee_info}
                  onChange={(e) => set('trustee_info', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Legal entity name" htmlFor="company-legal-entity-name">
                <TextInput
                  id="company-legal-entity-name"
                  value={form.legal_entity_name}
                  onChange={(e) => set('legal_entity_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Trading Name" htmlFor="company-trading-name">
                <TextInput
                  id="company-trading-name"
                  value={form.company_name}
                  onChange={(e) => set('company_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Business Address" htmlFor="company-address-line">
                <TextInput
                  id="company-address-line"
                  value={form.address_line}
                  onChange={(e) => set('address_line', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="City" htmlFor="company-city">
                <TextInput
                  id="company-city"
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="State" htmlFor="company-state">
                <TextInput
                  id="company-state"
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Postcode" htmlFor="company-postcode">
                <TextInput
                  id="company-postcode"
                  value={form.postcode}
                  onChange={(e) => set('postcode', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Country" htmlFor="company-country">
                <TextInput
                  id="company-country"
                  value={form.country}
                  onChange={(e) => set('country', e.target.value)}
                />
              </InfoRow>
              <InfoRow
                label={companyInfoLabel(settings, 'primaryContact')}
                htmlFor="company-primary-contact"
              >
                <TextInput
                  id="company-primary-contact"
                  value={form.primary_contact_name}
                  onChange={(e) => set('primary_contact_name', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact role/position" htmlFor="company-contact-role">
                <TextInput
                  id="company-contact-role"
                  value={form.contact_role}
                  onChange={(e) => set('contact_role', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact phone" htmlFor="company-contact-phone">
                <TextInput
                  id="company-contact-phone"
                  type="tel"
                  value={form.contact_phone}
                  onChange={(e) => set('contact_phone', e.target.value)}
                />
              </InfoRow>
              <InfoRow label="Contact email" htmlFor="company-contact-email">
                <TextInput
                  id="company-contact-email"
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                />
                {/* Saving this repoints the vendor's sign-in address and every
                    notification we send them, so say so where it's being typed. */}
                <p className="mt-1 text-xs text-slate-500">
                  This is the address the vendor signs in with — changing it changes their login.
                </p>
              </InfoRow>
              <InfoRow label="# of employees" htmlFor="company-employee-count">
                <TextInput
                  id="company-employee-count"
                  type="number"
                  min={0}
                  value={form.employee_count}
                  onChange={(e) => set('employee_count', e.target.value)}
                />
              </InfoRow>
            </div>
          ) : (
            <div>
              <InfoRow label="ABN">
                <ReadOnlyValue value={company.abn} />
                {abnNeedsAction(company.abn_verification_status) && (
                  <div className="mt-1">
                    <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
                      ABN needs action
                    </span>
                  </div>
                )}
                <div className="mt-0.5">
                  <AbnVerifiedBadge
                    status={company.abn_verification_status}
                    entityName={company.abn_entity_name}
                    registryStatus={company.abn_registry_status}
                  />
                </div>
                {company.abn && canWrite(user?.role) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Btn
                      size="sm"
                      variant="secondary"
                      onClick={() => reverifyAbnMutation.mutate()}
                      loading={reverifyAbnMutation.isPending}
                    >
                      Recheck ABN and GST
                    </Btn>
                    {reverifyNote && <span className="text-xs text-amber-600">{reverifyNote}</span>}
                    {reverifyAbnMutation.error != null && (
                      <span className="text-xs text-red-600">
                        Re-check failed — please try again.
                      </span>
                    )}
                  </div>
                )}
              </InfoRow>
              <InfoRow label="ABN registry status">
                <ReadOnlyValue value={company.abn_registry_status} />
              </InfoRow>
              <InfoRow label="GST status">
                {/* Server-written by the ABR checker. true = registered (with
                    ABR's effective-from date when known), false = definitively
                    not registered, null = never checked / ABN invalid /
                    register unreachable.
                    A confirmed false DOES block compliance — resolveCompanyStatus
                    treats it as hasUnregisteredGst, identically to an invalid
                    ABN. Only null is informational. */}
                {company.gst_registered === true ? (
                  <span className="font-medium text-slate-900">
                    ✓ Registered
                    {company.gst_registered_from
                      ? ` — since ${formatDate(company.gst_registered_from)}`
                      : ''}
                  </span>
                ) : gstBlocksCompliance(company.gst_registered) ? (
                  <span className="font-medium text-red-700">
                    Not registered — blocks compliant
                  </span>
                ) : (
                  <span className="text-slate-400">—</span>
                )}
              </InfoRow>
              <InfoRow label="Trustee Name and ACN">
                <ReadOnlyValue value={company.trustee_info} />
              </InfoRow>
              <InfoRow label="Legal entity name">
                {(() => {
                  // Task 19: on a mismatch the ABN registry name leads and the
                  // vendor's entered value is shown beneath it in parentheses.
                  const { headline, entered } = legalNameDisplay(
                    company.abn_entity_name,
                    company.legal_entity_name,
                  );
                  return (
                    <>
                      <ReadOnlyValue value={headline} />
                      {entered && (
                        <div className="text-xs text-slate-500">(entered: {entered})</div>
                      )}
                    </>
                  );
                })()}
              </InfoRow>
              <InfoRow label="Trading Name">
                {/* companies.company_name IS the trading name (0070). Renaming
                    here propagates everywhere the name is read: lists, headings,
                    emails, and the vendor portal. */}
                <ReadOnlyValue value={company.company_name} />
              </InfoRow>
              <InfoRow label="Business Address">
                <ReadOnlyValue value={company.address_line} />
              </InfoRow>
              <InfoRow label="City">
                <ReadOnlyValue value={company.city} />
              </InfoRow>
              <InfoRow label="State">
                <ReadOnlyValue value={company.state} />
              </InfoRow>
              <InfoRow label="Postcode">
                <ReadOnlyValue value={company.postcode} />
              </InfoRow>
              <InfoRow label="Country">
                <ReadOnlyValue value={company.country} />
              </InfoRow>
              <InfoRow label={companyInfoLabel(settings, 'primaryContact')}>
                <ReadOnlyValue value={company.primary_contact_name} />
              </InfoRow>
              <InfoRow label="Contact role/position">
                <ReadOnlyValue value={company.contact_role} />
              </InfoRow>
              <InfoRow label="Contact phone">
                <ReadOnlyValue value={company.contact_phone} />
              </InfoRow>
              <InfoRow label="Contact email">
                <ReadOnlyValue value={company.contact_email} />
              </InfoRow>
              <InfoRow label="# of employees">
                <ReadOnlyValue
                  value={company.employee_count === null ? null : String(company.employee_count)}
                />
              </InfoRow>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// One label/value row: label in a fixed-ish left column, arbitrary content
// (a read-only value or an input) on the right, with a divider between rows.
// Stacks on narrow screens; two columns at sm and up.
function InfoRow({
  label,
  htmlFor,
  align = 'center',
  children,
}: {
  label: string;
  htmlFor?: string;
  // Multi-line controls (the notes textarea) need the label at the top; a
  // centred label floats halfway down a 4-row box. Defaults to 'center' so the
  // single-line rows are untouched.
  align?: 'center' | 'start';
  children: ReactNode;
}) {
  return (
    <div
      data-info-label={label}
      className={`grid grid-cols-1 gap-1 border-b border-slate-100 py-3 last:border-b-0
                 sm:grid-cols-[minmax(160px,32%)_1fr] sm:gap-4 ${
                   align === 'start' ? 'sm:items-start' : 'sm:items-center'
                 }`}
    >
      {htmlFor ? (
        <label htmlFor={htmlFor} className="text-sm text-slate-500">
          {label}
        </label>
      ) : (
        <div className="text-sm text-slate-500">{label}</div>
      )}
      <div className="text-sm">{children}</div>
    </div>
  );
}

// Read-only value for the right column of an InfoRow: bold when present, an
// italic "Not provided" placeholder when empty.
function ReadOnlyValue({ value }: { value: string | null }) {
  return value && value.trim().length > 0 ? (
    // whitespace-pre-line so a multi-line note keeps its line breaks: HTML
    // collapses newlines, so without it a three-line note renders as one line.
    // No-op for the single-line fields, which contain no newlines.
    <span className="whitespace-pre-line font-medium text-slate-900">{value}</span>
  ) : (
    <span className="italic text-slate-400">Not provided</span>
  );
}

function SendQuestionnaireModal({
  companyId,
  options,
  onClose,
  onSent,
}: {
  companyId: string;
  options: SendOption[];
  onClose: () => void;
  onSent: () => void;
}) {
  const [selectedId, setSelectedId] = useState(firstSendableId(options));
  const [error, setError] = useState<unknown>(null);

  const sendMutation = useMutation({
    mutationFn: () =>
      // Task 12: the vendor is always notified — the opt-out checkbox is gone.
      adminApi.post(`/api/companies/${companyId}/questionnaires/${selectedId}/send`, {
        notify: true,
      }),
    onSuccess: onSent,
    onError: setError,
  });

  const selected = options.find((q) => q.id === selectedId);
  const { verb, alreadySent } = describeSend(selected);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

  return (
    <Modal
      open
      onClose={onClose}
      title={`${verb} questionnaire`}
      size="sm"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            onClick={() => sendMutation.mutate()}
            loading={sendMutation.isPending}
            disabled={!selectedId || (selected ? isResendBlocked(selected) : true)}
          >
            {verb}
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Questionnaire" required>
          <Select value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
            {options.map((q) => (
              <option key={q.id} value={q.id} disabled={isResendBlocked(q)}>
                {optionLabel(q, fmtDate)}
              </option>
            ))}
          </Select>
          {selectedId === '' && options.length > 0 && (
            <p className="text-xs text-slate-500">
              These questionnaires are already compliant — there is nothing to resend.
            </p>
          )}
        </Field>
        {alreadySent && selected?.sent_at && (
          <p className="text-xs text-slate-500">
            Already sent on {fmtDate(selected.sent_at)}. Sending again re-notifies the vendor; it
            won&rsquo;t change their compliance status.
          </p>
        )}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
